import { excludeNullOrUndefined } from 'checkout/utils'
import createSelector from 'lib/createSelector'
import { checkoutAccommodationOfferView, getAccommodationBreakdownView } from './accommodation'
import { getExperienceBreakdownView, getExperienceItems, getExperienceItemsView, getTransferItemsView, getTransfersBreakdownView } from './experience'
import { cartIncludesFlights, getFlightBreakdownView, getFlightItemsView } from './flights'
import { getInsuranceBreakdownView, getInsuranceItems, getInsuranceItemsView } from './insurance'
import { getCarHireBreakdownView } from './carHire'
import { getBundleAndSaveBreakdownView } from './bundleAndSave'
import { getGiftCardBreakdownView } from 'checkout/selectors/view/giftCard'
import { getVillaBreakdownView } from './villa'
import { getCustomOfferBreakdownView } from 'checkout/selectors/view/customOffer'
import { getLuxuryPlusBreakdownView } from 'checkout/selectors/view/luxuryPlusSubscription'

export const breakdownView = createSelector(
  getAccommodationBreakdownView,
  getFlightBreakdownView,
  getExperienceBreakdownView,
  getTransfersBreakdownView,
  getInsuranceBreakdownView,
  getCarHireBreakdownView,
  getBundleAndSaveBreakdownView,
  getGiftCardBreakdownView,
  getVillaBreakdownView,
  getCustomOfferBreakdownView,
  getLuxuryPlusBreakdownView,
  (...views): App.WithDataStatus<Array<App.Checkout.PriceBreakdownView>> => {
    const allViews = views.flatMap(view => view.data).filter(excludeNullOrUndefined)

    return {
      hasRequiredData: views.every(o => o.hasRequiredData),
      data: allViews,
    }
  },
)

export type AllCheckoutItemViews = ReturnType<typeof getAllItemViews>

function allHaveRequiredData(...objectsWithDataStatuses: Array<App.WithDataStatus<any>>): boolean {
  return objectsWithDataStatuses.every(o => o.hasRequiredData)
}

export const getAllItemViews = createSelector(
  checkoutAccommodationOfferView,
  getExperienceItemsView,
  getFlightItemsView,
  getInsuranceItemsView,
  getTransferItemsView,
  (...views) => {
    return {
      hasRequiredData: allHaveRequiredData(...views),
      data: {
        accommodationItemsView: views[0],
        experienceItemsView: views[1],
        flightItemsView: views[2],
        insuranceItemsView: views[3],
        transferItemsView: views[4],
      },
    }
  },
)

export const hasPurchasableItems = createSelector(
  getAllItemViews,
  (allItemViews) => {
    if (!allItemViews.hasRequiredData) {
      return {
        hasRequiredData: false,
        data: false,
      }
    }
    return (
      allItemViews.data.accommodationItemsView.data.length > 0 ||
      allItemViews.data.flightItemsView.data.length > 0 ||
      allItemViews.data.insuranceItemsView.data.length > 0 ||
      allItemViews.data.transferItemsView.data.length > 0 ||
      allItemViews.data.experienceItemsView.data.some(item => (
        item.ticketViews.some(ticket => !ticket.unavailable)
      ))
    )
  },
)

export const isAllOffersLoaded = createSelector(
  (state: App.State) => state.checkout.isCartRestored,
  checkoutAccommodationOfferView,
  getExperienceItemsView,
  getInsuranceItemsView,
  (isCartRestored, offerView, experienceItemsView, insuranceItemsView) => {
    return (
      isCartRestored &&
      offerView.data.every(view => view.offerLoaded) &&
      experienceItemsView.hasRequiredData &&
      insuranceItemsView.hasRequiredData
    )
  },
)

export const isSingleDesignation = createSelector(
  checkoutAccommodationOfferView,
  getExperienceItemsView,
  getFlightItemsView,
  (checkoutAccommodationOfferView, getExperienceItemsView, getFlightItemsView): boolean => {
    const allItemsViews = [...checkoutAccommodationOfferView.data, ...getExperienceItemsView.data, ...getFlightItemsView.data]
    return [...new Set(allItemsViews.map(view => view?.designation).filter(excludeNullOrUndefined))].length === 1
  },
)

export const checkoutHasNonAccommodationItems = createSelector(
  getInsuranceItems,
  cartIncludesFlights,
  getExperienceItems,
  (insuranceItems, includesFlights, experienceItems) => {
    return insuranceItems.length > 0 || includesFlights || experienceItems.length > 0
  },
)
